/**
 * Dashboard — premium overview: greeting hero → 4 KPI cards (clickable,
 * hover-lift, animated count-up, gradient layers) → AR ageing (1.6fr) + cash
 * position (1fr) → needs-attention + recent activity. All figures are derived
 * live from the billing services (documents, payments, bank lines); nothing is
 * hard-coded. Content fades/slides in on mount with a staggered cadence.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Animated, Easing, Platform, Pressable, Text, View } from 'react-native';
import { useTheme } from '../context/themeContext';
import { useBaseCurrency } from '../context/currencyContext';
import { useSession } from '../context/sessionContext';
import { useResponsive, MAX_CONTENT_WIDTH } from '../utils/responsive';
import SkIcon from '../components/skIcon';
import { fmtMoneyShort, fmtMoney, money } from '../utils/format';
import { listDocuments } from '../services/billing/document.service';
import { listPayments } from '../services/billing/payment.service';
import { listBankLines } from '../services/billing/banking.service';
import type { AppScreen } from '../navigation/types';

interface Props {
  onNavigate: (s: AppScreen) => void;
}

const isWeb = Platform.OS === 'web';
const ACTIVITY_NOUN: Record<string, string> = { INVOICE: 'Invoice', QUOTE: 'Quote', CREDIT_NOTE: 'Credit note', BILL: 'Bill', VENDOR_CREDIT: 'Vendor credit', EXPENSE: 'Expense' };
const web = (s: Record<string, unknown>): any => (isWeb ? s : {});
const useNative = !isWeb;

const daysOverdue = (due: string | null): number => {
  if (!due) return 0;
  const d = new Date(due);
  if (isNaN(d.getTime())) return 0;
  return Math.floor((Date.now() - d.getTime()) / 86400000);
};

const OPEN_STATUSES = ['ISSUED', 'PARTIALLY_PAID', 'OVERDUE'];

/** Entrance wrapper — fades up its children once, after `delay` ms. */
const FadeInUp: React.FC<{ delay?: number; style?: any; children: React.ReactNode }> = ({ delay = 0, style, children }) => {
  const a = useRef(new Animated.Value(0)).current;
  useEffect(() => {
    Animated.timing(a, { toValue: 1, duration: 460, delay, easing: Easing.out(Easing.cubic), useNativeDriver: useNative }).start();
  }, [a, delay]);
  return (
    <Animated.View
      style={[style, { opacity: a, transform: [{ translateY: a.interpolate({ inputRange: [0, 1], outputRange: [14, 0] }) }] }]}
    >
      {children}
    </Animated.View>
  );
};

/** Number that counts up from 0 → value once data lands. */
const CountUp: React.FC<{ value: number; format: (n: number) => string; loading: boolean; delay?: number; style: any }> = ({
  value,
  format,
  loading,
  delay = 0,
  style,
}) => {
  const a = useRef(new Animated.Value(0)).current;
  const [display, setDisplay] = useState(0);
  useEffect(() => {
    if (loading) return;
    const id = a.addListener(({ value: v }) => setDisplay(v));
    a.setValue(0);
    Animated.timing(a, { toValue: value, duration: 1000, delay, easing: Easing.out(Easing.cubic), useNativeDriver: false }).start();
    return () => a.removeListener(id);
  }, [a, value, loading, delay]);
  return <Text style={style}>{loading ? '—' : format(display)}</Text>;
};

interface Kpi {
  label: string;
  raw: number;
  format: (n: number) => string;
  delta: string;
  deltaColor: string;
  note: string;
  bg: string;
  ic: string;
  icon: string;
  screen: AppScreen;
}

/** Elevated, clickable KPI card — gradient icon tile, decorative glow layer,
 *  animated count-up, hover-lift, and a top accent bar that reveals on hover. */
const KpiCard: React.FC<{ k: Kpi; loading: boolean; delay: number; onPress: () => void }> = ({ k, loading, delay, onPress }) => {
  const { theme } = useTheme();
  const { colors, font } = theme;
  const [hover, setHover] = useState(false);

  return (
    <Pressable
      onPress={onPress}
      style={{
        position: 'relative',
        overflow: 'hidden',
        backgroundColor: colors.card,
        borderWidth: 1,
        borderColor: hover ? colors.lineStrong : colors.line,
        borderRadius: 14,
        padding: 16,
        ...theme.shadow(hover ? 'md' : 'sm'),
        ...web({
          transition: 'transform .18s ease, box-shadow .18s ease, border-color .18s ease',
          transform: hover ? 'translateY(-3px)' : 'translateY(0)',
          cursor: 'pointer',
        }),
      }}
      {...(isWeb ? { onMouseEnter: () => setHover(true), onMouseLeave: () => setHover(false) } : {})}
    >
      {/* Top accent bar (reveals on hover) */}
      <View
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: 3,
          backgroundColor: k.ic,
          opacity: hover ? 1 : 0,
          ...web({ backgroundImage: `linear-gradient(90deg, ${k.ic}, ${k.ic}00)`, transition: 'opacity .18s ease' }),
        }}
      />
      {/* Decorative blurred glow blob, top-right */}
      <View
        style={{
          position: 'absolute',
          top: -28,
          right: -28,
          width: 96,
          height: 96,
          borderRadius: 48,
          backgroundColor: k.bg,
          opacity: isWeb ? 0.7 : 0.35,
          ...web({ filter: 'blur(10px)' }),
        }}
        pointerEvents="none"
      />

      <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <Text style={{ fontFamily: font.sansMedium, fontSize: 12, color: colors.ink3, fontWeight: '500' }}>{k.label}</Text>
        <View
          style={{
            width: 34,
            height: 34,
            borderRadius: 10,
            backgroundColor: k.bg,
            alignItems: 'center',
            justifyContent: 'center',
            ...web({
              backgroundImage: `linear-gradient(145deg, ${k.bg}, ${colors.card})`,
              boxShadow: hover ? `0 6px 14px -3px ${k.ic}55` : `0 4px 10px -3px ${k.ic}40`,
              transition: 'box-shadow .18s ease',
            }),
          }}
        >
          <SkIcon name={k.icon} size={16} color={k.ic} strokeWidth={2.1} />
        </View>
      </View>
      <CountUp
        value={k.raw}
        format={k.format}
        loading={loading}
        delay={delay + 120}
        style={{ fontFamily: font.monoSemibold, fontSize: 24, fontWeight: '600', color: colors.ink, letterSpacing: -0.5 }}
      />
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 5, marginTop: 6 }}>
        {!!k.delta && <Text style={{ fontFamily: font.sansSemibold, fontSize: 11, color: k.deltaColor, fontWeight: '600' }}>{k.delta}</Text>}
        <Text style={{ fontFamily: font.sans, fontSize: 11, color: colors.ink4 }}>{k.note}</Text>
      </View>
    </Pressable>
  );
};

interface Attn {
  title: string;
  sub: string;
  screen: AppScreen;
  bg: string;
  ic: string;
  icon: string;
}

/** Hover-reactive row in the "needs attention" list. */
const AttentionRow: React.FC<{ a: Attn; onPress: () => void }> = ({ a, onPress }) => {
  const { theme } = useTheme();
  const { colors, font } = theme;
  const [hover, setHover] = useState(false);

  return (
    <Pressable
      onPress={onPress}
      style={{
        flexDirection: 'row',
        alignItems: 'center',
        gap: 11,
        padding: 10,
        borderRadius: 9,
        backgroundColor: hover ? colors.paper2 : 'transparent',
        ...web({ transition: 'background-color .14s ease', cursor: 'pointer' }),
      }}
      {...(isWeb ? { onMouseEnter: () => setHover(true), onMouseLeave: () => setHover(false) } : {})}
    >
      <View
        style={{
          width: 32,
          height: 32,
          borderRadius: 9,
          backgroundColor: a.bg,
          alignItems: 'center',
          justifyContent: 'center',
          ...web({ backgroundImage: `linear-gradient(145deg, ${a.bg}, ${colors.card})` }),
        }}
      >
        <SkIcon name={a.icon} size={15} color={a.ic} strokeWidth={2.1} />
      </View>
      <View style={{ flex: 1, minWidth: 0 }}>
        <Text style={{ fontFamily: font.sansSemibold, fontSize: 12.5, fontWeight: '600', color: colors.ink }}>{a.title}</Text>
        <Text style={{ fontFamily: font.sans, fontSize: 11, color: colors.ink3, marginTop: 1 }}>{a.sub}</Text>
      </View>
      <View style={{ ...web({ transform: hover ? 'translateX(2px)' : 'translateX(0)', transition: 'transform .14s ease' }) }}>
        <SkIcon name="chevRight" size={15} color={hover ? colors.ink3 : colors.ink4} strokeWidth={2} />
      </View>
    </Pressable>
  );
};

/** A glossy ageing segment: scales in on mount, shows its % inline, lifts +
 *  reveals a tooltip on hover. Empty buckets render nothing in the bar. */
const AgeingSegment: React.FC<{ label: string; amount: number; total: number; from: string; to: string; delay: number }> = ({
  label,
  amount,
  total,
  from,
  to,
  delay,
}) => {
  const { theme } = useTheme();
  const { baseCurrency: cur } = useBaseCurrency();
  const { colors, font } = theme;
  const a = useRef(new Animated.Value(0)).current;
  const [hover, setHover] = useState(false);
  const pct = total > 0 ? (amount / total) * 100 : 0;
  useEffect(() => {
    Animated.timing(a, { toValue: 1, duration: 700, delay, easing: Easing.out(Easing.cubic), useNativeDriver: useNative }).start();
  }, [a, delay]);
  if (amount <= 0) return null;
  return (
    <Animated.View
      style={{
        flex: Math.max(amount, 0),
        minWidth: 6,
        marginHorizontal: 1.5,
        opacity: a,
        transform: [{ scaleX: a }],
        ...web({ transformOrigin: 'left' as any }),
      }}
    >
      <Pressable
        style={{
          height: '100%',
          borderRadius: 8,
          overflow: 'hidden',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: to,
          ...web({
            backgroundImage: `linear-gradient(135deg, ${from}, ${to})`,
            boxShadow: hover ? `0 6px 18px -3px ${to}cc, inset 0 1px 0 rgba(255,255,255,0.35)` : `0 2px 8px -3px ${to}88, inset 0 1px 0 rgba(255,255,255,0.25)`,
            filter: hover ? 'brightness(1.08) saturate(1.1)' : 'brightness(1)',
            transition: 'box-shadow .15s ease, filter .15s ease',
            cursor: 'pointer',
          }),
        }}
        {...(isWeb ? { onMouseEnter: () => setHover(true), onMouseLeave: () => setHover(false) } : {})}
      >
        {/* glossy top sheen */}
        <View
          style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '52%', ...web({ backgroundImage: 'linear-gradient(180deg, rgba(255,255,255,0.38), rgba(255,255,255,0))' }) }}
          pointerEvents="none"
        />
        {pct >= 11 && <Text style={{ fontFamily: font.monoSemibold, fontSize: 11, fontWeight: '600', color: '#fff', ...web({ textShadow: '0 1px 2px rgba(0,0,0,0.25)' }) }}>{Math.round(pct)}%</Text>}
      </Pressable>
      {/* hover tooltip */}
      {isWeb && hover && (
        <View
          style={{
            position: 'absolute',
            bottom: '128%',
            left: '50%',
            backgroundColor: colors.ink,
            borderRadius: 8,
            paddingVertical: 6,
            paddingHorizontal: 11,
            zIndex: 50,
            ...theme.shadow('md'),
            ...web({ transform: 'translateX(-50%)', whiteSpace: 'nowrap' }),
          }}
          pointerEvents="none"
        >
          <Text style={{ fontFamily: font.sansSemibold, fontSize: 11, fontWeight: '600', color: colors.card, textAlign: 'center' }}>{label}</Text>
          <Text style={{ fontFamily: font.monoSemibold, fontSize: 12, color: colors.card, textAlign: 'center', marginTop: 1 }}>
            {fmtMoney(amount, cur)} · {Math.round(pct)}%
          </Text>
        </View>
      )}
    </Animated.View>
  );
};

const Dashboard: React.FC<Props> = ({ onNavigate }) => {
  const { theme } = useTheme();
  const { baseCurrency: cur } = useBaseCurrency();
  const { colors, font } = theme;
  const { user } = useSession();
  const { isWide, isMobile, width } = useResponsive();

  const sans = font.sans;

  const [docs, setDocs] = useState<any[]>([]);
  const [payments, setPayments] = useState<any[]>([]);
  const [unmatched, setUnmatched] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    Promise.allSettled([listDocuments(), listPayments(), listBankLines('UNMATCHED')])
      .then(([d, p, b]) => {
        if (!alive) return;
        if (d.status === 'fulfilled' && Array.isArray(d.value)) setDocs(d.value);
        if (p.status === 'fulfilled' && Array.isArray(p.value)) setPayments(p.value);
        if (b.status === 'fulfilled' && Array.isArray(b.value)) setUnmatched(b.value.length);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, []);

  const m = useMemo(() => {
    const bal = (d: any) => money(d.grand_total) - money(d.amount_settled);
    const invoices = docs.filter((d) => d.doc_type === 'INVOICE');
    const bills = docs.filter((d) => d.doc_type === 'BILL');
    const openInv = invoices.filter((d) => OPEN_STATUSES.includes(d.status));
    const overdueInv = openInv.filter((d) => d.status === 'OVERDUE' || daysOverdue(d.due_date) > 0);
    const openBills = bills.filter((d) => OPEN_STATUSES.includes(d.status));

    const now = new Date();
    const paidThisMonth = payments
      .filter((p) => p.direction === 'RECEIVED' && !p.is_refund)
      .filter((p) => {
        const pd = new Date(p.payment_date);
        return !isNaN(pd.getTime()) && pd.getMonth() === now.getMonth() && pd.getFullYear() === now.getFullYear();
      })
      .reduce((s, p) => s + money(p.amount), 0);

    const outstanding = openInv.reduce((s, d) => s + bal(d), 0);
    const overdueSum = overdueInv.reduce((s, d) => s + bal(d), 0);
    const payable = openBills.reduce((s, d) => s + bal(d), 0);
    const aiDrafts = docs.filter((d) => d.status === 'DRAFT' && d.created_via === 'AI').length;

    // AR ageing buckets from open invoices. Labels are written in plain
    // language so a non-finance user understands them at a glance. Each bucket
    // carries a premium two-stop gradient (lighter → richer) used by the bar
    // and the legend dot.
    const defs = [
      { label: 'Not due yet', from: '#34d399', to: '#059669', lo: -Infinity, hi: 0 },
      { label: '1–30 days late', from: '#60a5fa', to: '#2563eb', lo: 1, hi: 30 },
      { label: '31–60 days late', from: '#fbbf24', to: '#d97706', lo: 31, hi: 60 },
      { label: '61–90 days late', from: '#fb7185', to: '#e11d48', lo: 61, hi: 90 },
      { label: 'Over 90 days late', from: '#a78bfa', to: '#7c3aed', lo: 91, hi: Infinity },
    ];
    const sums = defs.map(() => 0);
    openInv.forEach((d) => {
      const od = daysOverdue(d.due_date);
      const i = defs.findIndex((b) => od >= b.lo && od <= b.hi);
      if (i >= 0) sums[i] += bal(d);
    });
    const buckets = defs.map((b, i) => ({ label: b.label, amount: sums[i], from: b.from, to: b.to }));

    return { outstanding, overdueSum, overdueCount: overdueInv.length, payable, paidThisMonth, aiDrafts, buckets, openBillsCount: openBills.length };
  }, [docs, payments, colors]);

  const kpis: Kpi[] = [
    { label: 'Outstanding receivable', raw: m.outstanding, format: (n: number) => fmtMoneyShort(n, cur), delta: '', deltaColor: colors.ink3, note: 'open invoices', bg: colors.dangerSoft, ic: colors.danger, icon: 'rupee', screen: 'invoices' },
    { label: 'Overdue', raw: m.overdueSum, format: (n: number) => fmtMoneyShort(n, cur), delta: `${m.overdueCount} invoice${m.overdueCount === 1 ? '' : 's'}`, deltaColor: colors.warn, note: 'need follow-up', bg: colors.warnSoft, ic: colors.warn, icon: 'clock', screen: 'invoices' },
    { label: 'Paid this month', raw: m.paidThisMonth, format: (n: number) => fmtMoneyShort(n, cur), delta: '', deltaColor: colors.success, note: 'received', bg: colors.successSoft, ic: colors.success, icon: 'checkCircle', screen: 'payments' },
    { label: 'Payable', raw: m.payable, format: (n: number) => fmtMoneyShort(n, cur), delta: `${m.openBillsCount} open`, deltaColor: colors.ink3, note: 'bills', bg: colors.accentSoft, ic: colors.accent, icon: 'cart', screen: 'bills' },
  ];

  const totalAgeing = m.buckets.reduce((s, b) => s + b.amount, 0);
  const currentAmt = m.buckets[0]?.amount || 0; // "Not due yet"
  const overdueAmt = Math.max(totalAgeing - currentAmt, 0);
  const ageingHealthy = overdueAmt <= 0;
  const onTimePct = totalAgeing > 0 ? Math.round((currentAmt / totalAgeing) * 100) : 0;
  const attention = [
    unmatched > 0 ? { title: `${unmatched} bank line${unmatched === 1 ? '' : 's'} unmatched`, sub: 'Reconcile in the banking workbench', screen: 'banking' as AppScreen, bg: colors.accentSoft, ic: colors.accent, icon: 'bank' } : null,
    m.aiDrafts > 0 ? { title: `${m.aiDrafts} AI draft${m.aiDrafts === 1 ? '' : 's'} awaiting review`, sub: 'Resolve placeholders, then issue', screen: 'invoices' as AppScreen, bg: colors.warnSoft, ic: colors.warn, icon: 'file' } : null,
    m.overdueCount > 0 ? { title: `${m.overdueCount} invoice${m.overdueCount === 1 ? '' : 's'} overdue`, sub: `${fmtMoney(m.overdueSum, cur)} — send reminders`, screen: 'invoices' as AppScreen, bg: colors.warnSoft, ic: colors.warn, icon: 'clock' } : null,
  ].filter(Boolean) as Attn[];

  // KPI columns: 4 wide, 2 on tablet/narrow, 1 on small mobile.
  const kpiCols = width >= 900 ? 4 : width >= 560 ? 2 : 1;
  const kpiBasis = `${100 / kpiCols}%`;

  // Newest first, from the documents and payments already loaded above — no extra call.
  const activity = useMemo(() => {
    const at = (v: string | null | undefined) => { const t = new Date(v || '').getTime(); return isNaN(t) ? 0 : t; };
    const docItems = docs
      .filter((d) => d.status !== 'DRAFT' && d.doc_number)
      .map((d) => ({
        key: `d-${d.id}`,
        t: at(d.issued_at || d.doc_date),
        title: `${ACTIVITY_NOUN[d.doc_type] || 'Document'} ${d.doc_number}${d.status === 'VOID' ? ' voided' : ' issued'}`,
        sub: d.contact_name || '',
        amount: fmtMoney(money(d.grand_total), d.currency),
        dot: d.status === 'VOID' ? colors.ink4 : colors.info,
      }));
    const payItems = payments.map((p) => ({
      key: `p-${p.id}`,
      t: at(p.created_at || p.payment_date),
      title: p.direction === 'MADE' ? 'Payment made' : 'Payment received',
      sub: [p.contact_name, p.reference].filter(Boolean).join(' · '),
      amount: `${p.direction === 'MADE' ? '−' : '+'}${fmtMoney(money(p.amount), p.currency)}`,
      dot: p.direction === 'MADE' ? colors.warn : colors.success,
    }));
    return [...docItems, ...payItems].filter((x) => x.t > 0).sort((a, b) => b.t - a.t).slice(0, 6);
  }, [docs, payments, colors]);

  const card = { backgroundColor: colors.card, borderWidth: 1, borderColor: colors.line, borderRadius: 14, ...theme.shadow('sm') } as const;
  const cardHeader = { paddingVertical: 14, paddingHorizontal: 18, borderBottomWidth: 1, borderBottomColor: colors.line } as const;
  const h3 = { fontFamily: font.sansSemibold, fontSize: 13.5, fontWeight: '600' as const, color: colors.ink, letterSpacing: -0.1 };
  const subText = { fontFamily: sans, fontSize: 11, color: colors.ink3, marginTop: 2 };
  const emptyText = { fontFamily: sans, fontSize: 12, color: colors.ink4 };

  // Greeting hero
  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';
  const rawName = user?.firstName || 'there';
  const firstName = rawName.charAt(0).toUpperCase() + rawName.slice(1).toLowerCase();
  const subline = ageingHealthy
    ? 'Your books are in great shape — everything is on track today.'
    : "Here's your business at a glance — a few things could use your attention.";
  const dateLabel = new Date().toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long' });

  return (
    <View style={{ maxWidth: MAX_CONTENT_WIDTH, width: '100%', alignSelf: 'center', paddingHorizontal: isWide ? 28 : 16 }}>
      {/* Greeting hero */}
      <FadeInUp delay={0}>
        <View style={{ flexDirection: 'row', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10, marginBottom: 18 }}>
          <View style={{ minWidth: 0 }}>
            <Text style={{ fontFamily: font.sansBold, fontSize: 23, fontWeight: '700', color: colors.ink, letterSpacing: -0.6 }}>
              {greeting}, {firstName} 👋
            </Text>
            <Text style={{ fontFamily: sans, fontSize: 13, color: colors.ink3, marginTop: 4 }}>
              {subline}
            </Text>
          </View>
          <View
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              gap: 7,
              height: 30,
              paddingHorizontal: 12,
              borderRadius: 999,
              backgroundColor: colors.card,
              borderWidth: 1,
              borderColor: colors.line,
              ...theme.shadow('sm'),
            }}
          >
            <SkIcon name="calendar" size={14} color={colors.ink3} strokeWidth={2} />
            <Text style={{ fontFamily: font.sansMedium, fontSize: 12, fontWeight: '500', color: colors.ink2 }}>{dateLabel}</Text>
          </View>
        </View>
      </FadeInUp>

      {/* KPI row */}
      <View style={{ flexDirection: 'row', flexWrap: 'wrap', marginHorizontal: -7, marginBottom: 4 }}>
        {kpis.map((k, i) => (
          <FadeInUp key={k.label} delay={80 + i * 70} style={{ width: kpiBasis as any, padding: 7 }}>
            <KpiCard k={k} loading={loading} delay={80 + i * 70} onPress={() => onNavigate(k.screen)} />
          </FadeInUp>
        ))}
      </View>

      {/* AR ageing + Cash */}
      <View style={{ flexDirection: isWide ? 'row' : 'column', gap: 16, marginTop: 11, marginBottom: 16 }}>
        {/* Ageing */}
        <FadeInUp delay={360} style={{ flex: isWide ? 1.6 : undefined }}>
          <View style={card}>
            <View style={[cardHeader, { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }]}>
              <View style={{ flex: 1, minWidth: 0, paddingRight: 10 }}>
                <Text style={h3}>Who owes you, and how late</Text>
                <Text style={subText}>Money your customers still owe, grouped by how overdue each invoice is.</Text>
              </View>
              <Pressable onPress={() => onNavigate('reports')} style={{ height: 28, paddingHorizontal: 12, borderWidth: 1, borderColor: colors.line, borderRadius: 7, justifyContent: 'center', backgroundColor: colors.paper, ...web({ cursor: 'pointer' }) }}>
                <Text style={{ fontFamily: font.sansMedium, fontSize: 12, fontWeight: '500', color: colors.ink2 }}>AR report</Text>
              </Pressable>
            </View>
            <View style={{ padding: 18 }}>
              {totalAgeing <= 0 ? (
                <Text style={emptyText}>No outstanding receivables.</Text>
              ) : (
                <>
                  {/* Total outstanding */}
                  <View style={{ flexDirection: 'row', alignItems: 'baseline', gap: 8, marginBottom: 10 }}>
                    <Text style={{ fontFamily: font.monoSemibold, fontSize: 21, fontWeight: '600', color: colors.ink, letterSpacing: -0.5 }}>{fmtMoney(totalAgeing, cur)}</Text>
                    <Text style={{ fontFamily: sans, fontSize: 11, color: colors.ink4 }}>owed to you in total</Text>
                  </View>

                  {/* Plain-language status — tells the user what this means + what to do */}
                  <View
                    style={{
                      flexDirection: 'row',
                      alignItems: 'center',
                      gap: 9,
                      paddingVertical: 9,
                      paddingHorizontal: 12,
                      marginBottom: 16,
                      borderRadius: 9,
                      borderWidth: 1,
                      backgroundColor: ageingHealthy ? colors.successSoft : colors.warnSoft,
                      borderColor: ageingHealthy ? colors.successLine : colors.warnLine,
                    }}
                  >
                    <SkIcon name={ageingHealthy ? 'checkCircle' : 'alert'} size={16} color={ageingHealthy ? colors.success : colors.warn} strokeWidth={2.1} />
                    <Text style={{ flex: 1, fontFamily: font.sansMedium, fontSize: 12, fontWeight: '500', color: colors.ink2, lineHeight: 17 }}>
                      {ageingHealthy
                        ? `On track — every invoice is still within its due date. Nothing to chase right now.`
                        : `${fmtMoney(overdueAmt, cur)} is overdue across ${m.overdueCount} invoice${m.overdueCount === 1 ? '' : 's'}. ${onTimePct}% is still on time — follow up on the rest soon.`}
                    </Text>
                  </View>
                  {/* Recessed track holding glossy floating segments */}
                  <View
                    style={{
                      flexDirection: 'row',
                      height: 38,
                      borderRadius: 10,
                      padding: 2,
                      marginBottom: 18,
                      backgroundColor: colors.paper2,
                      borderWidth: 1,
                      borderColor: colors.line,
                      ...web({ boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.08)' }),
                    }}
                  >
                    {m.buckets.map((b, i) => (
                      <AgeingSegment key={b.label} label={b.label} amount={b.amount} total={totalAgeing} from={b.from} to={b.to} delay={420 + i * 90} />
                    ))}
                  </View>
                  {/* Chip legend with share % */}
                  <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8 }}>
                    {m.buckets.map((b) => {
                      const pct = totalAgeing > 0 ? Math.round((b.amount / totalAgeing) * 100) : 0;
                      const dim = b.amount <= 0;
                      return (
                        <View
                          key={b.label}
                          style={{
                            flexGrow: 1,
                            minWidth: isMobile ? '46%' : 120,
                            flexDirection: 'row',
                            alignItems: 'center',
                            gap: 9,
                            paddingVertical: 8,
                            paddingHorizontal: 11,
                            borderRadius: 9,
                            borderWidth: 1,
                            borderColor: colors.line,
                            backgroundColor: colors.paper,
                            opacity: dim ? 0.6 : 1,
                          }}
                        >
                          <View style={{ width: 11, height: 11, borderRadius: 4, backgroundColor: b.to, ...web({ backgroundImage: `linear-gradient(135deg, ${b.from}, ${b.to})`, boxShadow: `0 0 0 3px ${b.to}22` }) }} />
                          <View style={{ minWidth: 0 }}>
                            <Text style={{ fontFamily: sans, fontSize: 11, color: colors.ink3 }}>{b.label}</Text>
                            <View style={{ flexDirection: 'row', alignItems: 'baseline', gap: 5 }}>
                              <Text style={{ fontFamily: font.monoSemibold, fontSize: 13, fontWeight: '600', color: colors.ink }}>{fmtMoneyShort(b.amount, cur)}</Text>
                              <Text style={{ fontFamily: sans, fontSize: 10, color: colors.ink4 }}>{pct}%</Text>
                            </View>
                          </View>
                        </View>
                      );
                    })}
                  </View>
                </>
              )}
            </View>
          </View>
        </FadeInUp>

        {/* Cash */}
        <FadeInUp delay={440} style={{ flex: isWide ? 1 : undefined }}>
          <View style={[card, { overflow: 'hidden' }]}>
            <View style={cardHeader}>
              <Text style={h3}>Cash position</Text>
              <Text style={subText}>Reconcile bank lines to see balances</Text>
            </View>
            <View style={{ padding: 18 }}>
              <Text style={{ fontFamily: sans, fontSize: 11, color: colors.ink4, fontWeight: '500', textTransform: 'uppercase', letterSpacing: 0.6 }}>Received this month</Text>
              <CountUp
                value={m.paidThisMonth}
                format={(n: number) => fmtMoney(n, cur)}
                loading={loading}
                delay={520}
                style={{ fontFamily: font.monoSemibold, fontSize: 30, fontWeight: '600', color: colors.ink, letterSpacing: -0.8, marginTop: 4 }}
              />
              <Pressable
                onPress={() => onNavigate('banking')}
                style={{
                  marginTop: 16,
                  height: 36,
                  flexDirection: 'row',
                  gap: 8,
                  backgroundColor: colors.accentSoft,
                  borderWidth: 1,
                  borderColor: colors.accentLine,
                  borderRadius: 9,
                  alignItems: 'center',
                  justifyContent: 'center',
                  ...web({ cursor: 'pointer' }),
                }}
              >
                <SkIcon name="bank" size={15} color={colors.accentInk} strokeWidth={2} />
                <Text style={{ fontFamily: font.sansSemibold, fontSize: 12.5, fontWeight: '600', color: colors.accentInk }}>
                  {unmatched > 0 ? `Go to banking · ${unmatched} unmatched` : 'Go to banking'}
                </Text>
              </Pressable>
            </View>
          </View>
        </FadeInUp>
      </View>

      {/* Attention + Activity */}
      <View style={{ flexDirection: isWide ? 'row' : 'column', gap: 16, paddingBottom: 8 }}>
        <FadeInUp delay={520} style={{ flex: 1 }}>
          <View style={card}>
            <View style={cardHeader}><Text style={h3}>Needs your attention</Text></View>
            <View style={{ padding: 8 }}>
              {attention.length === 0 ? (
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 9, padding: 10 }}>
                  {!loading && (
                    <View style={{ width: 26, height: 26, borderRadius: 8, backgroundColor: colors.successSoft, alignItems: 'center', justifyContent: 'center' }}>
                      <SkIcon name="checkCircle" size={14} color={colors.success} strokeWidth={2.1} />
                    </View>
                  )}
                  <Text style={emptyText}>{loading ? 'Loading…' : 'All clear — nothing needs attention.'}</Text>
                </View>
              ) : (
                attention.map((a) => <AttentionRow key={a.title} a={a} onPress={() => onNavigate(a.screen)} />)
              )}
            </View>
          </View>
        </FadeInUp>

        <FadeInUp delay={600} style={{ flex: 1 }}>
          <View style={card}>
            <View style={cardHeader}><Text style={h3}>Recent activity</Text></View>
            {activity.length === 0 ? (
              <View style={{ paddingHorizontal: 18, paddingVertical: 18 }}>
                <Text style={emptyText}>{loading ? 'Loading…' : 'Activity will appear here as you issue documents and record payments.'}</Text>
              </View>
            ) : (
              <View style={{ paddingVertical: 6 }}>
                {activity.map((a, i) => (
                  <View key={a.key} style={{ flexDirection: 'row', alignItems: 'center', gap: 11, paddingVertical: 10, paddingHorizontal: 18, borderTopWidth: i === 0 ? 0 : 1, borderTopColor: colors.line }}>
                    <View style={{ width: 8, height: 8, borderRadius: 4, backgroundColor: a.dot }} />
                    <View style={{ flex: 1, minWidth: 0 }}>
                      <Text numberOfLines={1} style={{ fontFamily: font.sansMedium, fontSize: 12.5, fontWeight: '500', color: colors.ink }}>{a.title}</Text>
                      {!!a.sub && <Text numberOfLines={1} style={{ fontFamily: sans, fontSize: 11, color: colors.ink4, marginTop: 1 }}>{a.sub}</Text>}
                    </View>
                    <Text style={{ fontFamily: font.monoMedium, fontSize: 12, color: colors.ink2 }}>{a.amount}</Text>
                  </View>
                ))}
              </View>
            )}
          </View>
        </FadeInUp>
      </View>
    </View>
  );
};

export default Dashboard;
